/**
 * REL-24 — drives the StoreKit entitlement reconcile from the shell.
 *
 * Renders nothing; mounted once at the app root (beside HealthSyncMount) so it
 * lives across the whole signed-in native session.
 *
 * WHY IT EXISTS: entitlement must not depend on someone opening Settings. At
 * launch the app reads `Transaction.currentEntitlements` and posts them to the
 * server, which verifies and stamps. A subscription bought on another device,
 * restored after a reinstall, or approved through Ask to Buy lands here.
 *
 * It is also the DOWNGRADE path's partner: when Apple reports no entitlement
 * and this account's stored StoreKit period has passed, the server drops the
 * tier. The server does not trust this mount for that — `getUserTier` enforces
 * the same expiry on its own, so a client that never calls gains nothing.
 *
 * Same discipline as HealthSyncMount: native + signed-in + flag-gated,
 * best-effort, never throws.
 */

import { useEffect } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { useServerFlag } from '../../hooks/useServerFlag';
import { isNativeApp } from '../../lib/platform';
import { onTransactionUpdate, syncCurrentEntitlements, syncEntitlement } from '../../lib/billing/storeKit';

export function StoreKitSyncMount(): null {
  const { user } = useAuthContext();
  const fetchAuthed = useAuthFetch();
  const storeKitOn = useServerFlag('storekit_v1');

  useEffect(() => {
    if (!isNativeApp() || !user || !storeKitOn) return;
    let alive = true;

    // App open = the launch reconcile.
    void syncCurrentEntitlements(fetchAuthed, 'launch');

    // `Transaction.updates`: Ask to Buy approvals, renewals, a purchase made
    // on another device, a Family Sharing change. Each arrives as a signed
    // transaction and goes straight to the server.
    let detachUpdates: (() => void) | null = null;
    void (async () => {
      const remove = await onTransactionUpdate((jws) => {
        if (alive) void syncEntitlement(fetchAuthed, [jws], 'update');
      });
      if (alive) detachUpdates = remove;
      else remove();
    })();

    // Warm foregrounds, via @capacitor/app's appStateChange — the same hook
    // HealthSyncMount uses. A subscription cancelled in Apple Account settings
    // is noticed on the return to the app.
    let detachState: (() => void) | null = null;
    void (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('appStateChange', ({ isActive }) => {
          if (isActive && alive) void syncCurrentEntitlements(fetchAuthed, 'launch');
        });
        detachState = () => void handle.remove();
      } catch {
        /* @capacitor/app unavailable — the mount-time sync still ran. */
      }
    })();

    return () => {
      alive = false;
      detachUpdates?.();
      detachState?.();
    };
  }, [user, storeKitOn, fetchAuthed]);

  return null;
}
